/* Progressive enhancement: native page copy remains readable without JavaScript. */
(() => {
  const productHandle = href => {
    try {
      const url = new URL(href, location.origin);
      if (url.origin !== location.origin) return null;
      const parts = url.pathname.split('/products/');
      return parts.length === 2 ? decodeURIComponent(parts[1].replace(/\/$/, '')) : null;
    } catch { return null; }
  };

  class BuyingGuide extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enhanced) return;
      const body = this.querySelector('[data-guide-body]');
      if (!body) return;
      this.dataset.enhanced = 'true';
      const intro = this.querySelector('[data-guide-intro]');
      const firstHeading = body.querySelector('h2');
      const opening = [];
      for (const child of body.children) { if (child === firstHeading) break; opening.push(child); }
      for (const paragraph of opening) {
        const text = paragraph.textContent.trim();
        if (/^Buying Guide\s*[|·]/i.test(text) || /^By the team\b/i.test(text)) paragraph.remove();
        else if (/^The short answer/i.test(text)) paragraph.classList.add('st-guide__answer');
        else if (intro) intro.append(paragraph);
      }
      for (const paragraph of body.querySelectorAll('p')) {
        if (/^The SoundTrap\s*[|·]/i.test(paragraph.textContent.trim()) && /Prices and stock current/.test(paragraph.textContent)) paragraph.remove();
      }

      const references = [...this.querySelectorAll('[data-guide-product]')];
      const byHandle = new Map(references.map(card => [card.dataset.handle, card]));
      // Replace exact quoted product prices, not general budgeting advice.
      for (const link of body.querySelectorAll('a[href]')) {
        const card = byHandle.get(productHandle(link.href));
        if (!card) continue;
        link.dataset.guideHandle = card.dataset.handle;
        const isViewLink = /^View\b/i.test(link.textContent.trim());
        const quotedPrice = /\$[\d,.]+/.test(link.textContent);
        // A fallback product is showing, so name it rather than the original pick.
        const substitute = card.dataset.substituted && card.dataset.url && !isViewLink;
        if (substitute) link.href = card.dataset.url;
        if (quotedPrice || substitute) {
          const label = substitute ? card.dataset.title : link.textContent.replace(/(?:\s+at|,)?\s*\$[\d,.]+/g, '').trim();
          const price = quotedPrice && card.querySelector('[data-guide-price]')?.textContent.trim();
          link.textContent = price ? `${label} (${price})` : label;
        }
        if (card.dataset.unlisted) {
          if (!/^View\b/i.test(link.textContent.trim())) link.textContent += ' (ask about availability)';
          link.href = '/pages/contact';
          link.dataset.guideUnavailable = 'true';
          link.title = 'Not currently listed online. Ask about availability.';
        }
      }

      const placed = new Set();
      for (const heading of [...body.querySelectorAll('h3')]) {
        const group = [];
        for (let next = heading.nextElementSibling; next && !/^H[23]$/.test(next.tagName); next = next.nextElementSibling) group.push(next);
        const link = group.flatMap(el => [...el.querySelectorAll('a[href]')]).find(a => /^View\b/i.test(a.textContent.trim()));
        // Unlisted links have already been changed to the contact path.
        const card = link && byHandle.get(link.dataset.guideHandle || productHandle(link.getAttribute('href')));
        if (!card || placed.has(card)) continue;
        placed.add(card);
        heading.before(card);
        heading.remove();
        const editorial = card.querySelector('.st-guide-product__editorial');
        for (const node of group) {
          if (node.contains(link)) {
            link.remove();
            if (!node.textContent.trim()) node.remove();
            break;
          }
          if (/^\$[\d,.]+(?:\s*(?:AUD|pair|each))?$/i.test(node.textContent.trim())) { node.remove(); continue; }
          // Card brings its own copy (fallback or rewritten pick): drop the page copy.
          if (card.dataset.ownEditorial) { node.remove(); continue; }
          if (card.dataset.sku === 'DAD-EPS530') node.innerHTML = node.innerHTML.replace(/\b0?8-39\b/g, '8-38');
          editorial.append(node);
        }
      }
      // Extra picks with no page copy lead the product cards in their named
      // section (they are chosen from sales data), or close it if it has none.
      for (const card of references) {
        if (placed.has(card) || !card.dataset.anchor) continue;
        const anchor = card.dataset.anchor.toLowerCase();
        const heading = [...body.querySelectorAll('h2')].find(h => h.textContent.trim().toLowerCase().startsWith(anchor));
        if (!heading) continue;
        let end = heading, firstCard = null;
        while (end.nextElementSibling && end.nextElementSibling.tagName !== 'H2') {
          end = end.nextElementSibling;
          if (!firstCard && end.matches('[data-guide-product]')) firstCard = end;
        }
        // Keep a card's small "Best for..." label paragraph with its card.
        const before = firstCard?.previousElementSibling;
        const target = before && /^STRONG$/.test(before.firstElementChild?.tagName || '') && before.textContent.trim() === before.firstElementChild.textContent.trim() ? before : firstCard;
        if (target) target.before(card); else end.after(card);
        // Match the page's own "Best for..." line above each card.
        const label = card.querySelector('.st-guide-product__label');
        if (label) {
          const line = document.createElement('p'); const strong = document.createElement('strong');
          strong.textContent = label.textContent; line.append(strong);
          card.before(line); label.remove();
        }
        placed.add(card);
      }
      const refContainer = this.querySelector('[data-guide-references]');
      if (refContainer && !refContainer.querySelector('[data-guide-product]')) refContainer.hidden = true;
      else if (refContainer) {
        const h2 = document.createElement('h2'); h2.textContent = 'Also mentioned'; refContainer.prepend(h2);
      }
      for (const table of body.querySelectorAll('table')) {
        const header = table.querySelector('tr');
        const priceColumns = [...(header?.children || [])].flatMap((cell, i) => /^(?:price|rrp)(?:\s|$)/i.test(cell.textContent.trim()) ? [i] : []);
        if (priceColumns.length) {
          for (const row of table.rows) {
            for (const index of [...priceColumns].reverse()) row.cells[index]?.remove();
          }
          const caption = table.createCaption();
          caption.textContent = 'See the product links for current pricing and availability.';
        }
        const wrapper = document.createElement('div');
        wrapper.className = 'st-guide__table'; wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region'); wrapper.setAttribute('aria-label', 'Comparison table');
        table.before(wrapper); wrapper.append(table);
      }
      const contents = this.querySelector('[data-guide-contents]');
      if (window.matchMedia?.('(max-width: 749px)').matches) this.querySelector('[data-guide-navigation] details')?.removeAttribute('open');
      const headings = [...body.querySelectorAll('h2')];
      headings.forEach((heading, i) => {
        heading.id ||= `guide-section-${i + 1}`;
        const item = document.createElement('li'); const link = document.createElement('a');
        link.href = `#${heading.id}`; link.textContent = heading.textContent;
        item.append(link); contents?.append(item);
      });
      if (headings.length) this.querySelector('[data-guide-navigation]')?.removeAttribute('hidden');
    }
  }
  if (!customElements.get('st-buying-guide')) customElements.define('st-buying-guide', BuyingGuide);
})();
